import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { Loader2, Database, Trash2, Sparkles } from "lucide-react";

/**
 * v5.7 Addition 23 — Load / Unload Sample Data (Settings sub-tab, VC Admin only).
 * One active load per pipeline. Fully repeatable — Load re-appears after Unload.
 */
export default function SampleData() {
  const { pipelineId, pipelines } = usePipelineCtx();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!pipelineId) return;
    api.get("/sample-data/status", { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setStatus(data));
  };
  useEffect(() => { load(); }, [pipelineId]); // eslint-disable-line

  const pipe = pipelines.find((p) => p.pipeline_id === pipelineId);
  const pipeName = pipe?.pipeline_name || "…";

  const doLoad = async () => {
    if (!window.confirm(
      `This will add 25 sample deals and their associated gates, agents, tasks, tags, and notes to ${pipeName}. You can remove all of this later with one click. Continue?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sample-data/load", { pipeline_id: pipelineId });
      toast.success(`Loaded ${data.deals_created} sample deals`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setBusy(false);
    }
  };

  const doUnload = async () => {
    if (!window.confirm(
      `This will permanently remove all 25 sample deals and everything associated with them from ${pipeName}. Real deals and configuration you have added are not affected. Continue?`
    )) return;
    setBusy(true);
    try {
      await api.post("/sample-data/unload", { pipeline_id: pipelineId });
      toast.success("Sample data unloaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="cw-card p-6" data-testid="sample-data-panel">
        <div className="flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(243,191,92,0.15)" }}
          >
            <Sparkles size={22} style={{ color: "var(--cw-secondary)" }} />
          </div>
          <div className="flex-1">
            <h3 className="cw-h3">Sample Data</h3>
            <p className="text-sm text-[var(--cw-muted)] mt-1">
              Populate <strong>{pipeName}</strong> with 25 curated demo deals,
              5 gates, 4 agents, 3 tags, and 3 custom fields. Remove everything
              with a single click.
            </p>
            {status?.loaded && status.load && (
              <div className="mt-3 text-xs text-[var(--cw-muted)]">
                Loaded on {status.load.loaded_at}. Load ID{" "}
                <span className="font-mono">{status.load.load_id?.slice(0, 8)}</span>.
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          {!status?.loaded ? (
            <button
              className="cw-btn-primary flex items-center gap-2"
              onClick={doLoad}
              disabled={busy || !pipelineId}
              data-testid="sample-data-load-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
              Load Sample Data into {pipeName}
            </button>
          ) : (
            <button
              className="cw-btn-secondary flex items-center gap-2"
              onClick={doUnload}
              disabled={busy}
              style={{ color: "var(--cw-error)" }}
              data-testid="sample-data-unload-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Unload Sample Data from {pipeName}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
